import { supabase } from '@/integrations/supabase/client';

// Cache server time offset to avoid repeated calls
let serverTimeOffset: number | null = null;
let lastFetchTime: number = 0;
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Clears the cached server time offset so the next call fetches fresh.
 */
export const clearServerTimeCache = () => {
  serverTimeOffset = null;
  lastFetchTime = 0;
};

/**
 * Fetches server time from Supabase RPC and caches the offset.
 * Returns server time as Date object.
 * @param forceRefresh - bypass cache and fetch fresh server time
 */
export const getServerTime = async (forceRefresh = false): Promise<Date> => {
  const now = Date.now();
  
  // Use cached offset if still valid (unless force refresh requested)
  if (!forceRefresh && serverTimeOffset !== null && (now - lastFetchTime) < CACHE_DURATION_MS) {
    return new Date(now + serverTimeOffset);
  }
  
  try {
    // Primary: use DB RPC for reliable server time (not affected by CORS)
    const { data, error } = await supabase.rpc('get_server_timestamp');
    
    if (!error && data) {
      const serverTime = new Date(data);
      serverTimeOffset = serverTime.getTime() - now;
      lastFetchTime = now;
      
      const driftMinutes = Math.round(serverTimeOffset / 1000 / 60);
      console.log(`Clock drift: ${driftMinutes} minutes (offset: ${serverTimeOffset}ms)`);
      
      return serverTime;
    }
    
    console.warn('get_server_timestamp RPC failed:', error?.message);
  } catch (err) {
    console.warn('Error fetching server time via RPC:', err);
  }

  // Fallback: HEAD request (may fail due to CORS stripping Date header)
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://xhoqjcdsprhrrvbminxe.supabase.co';
    const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || import.meta.env.VITE_SUPABASE_ANON_KEY || '';
    const response = await fetch(`${supabaseUrl}/rest/v1/`, {
      method: 'HEAD',
      headers: { 'apikey': supabaseKey },
    });
    
    const serverDateHeader = response.headers.get('date');
    if (serverDateHeader) {
      const serverTime = new Date(serverDateHeader);
      serverTimeOffset = serverTime.getTime() - now;
      lastFetchTime = now;
      
      const driftMinutes = Math.round(serverTimeOffset / 1000 / 60);
      console.log(`Clock drift (HEAD fallback): ${driftMinutes} minutes`);
      
      return serverTime;
    }
  } catch (err) {
    console.warn('HEAD fallback also failed:', err);
  }

  // Last resort: local time
  return new Date();
};

/**
 * Gets today's date in YYYY-MM-DD format using server time
 */
export const getServerTodayDate = async (): Promise<string> => {
  const serverTime = await getServerTime();
  return formatDateToYYYYMMDD(serverTime);
};

/**
 * Formats a Date to YYYY-MM-DD string
 */
export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Check if client time differs significantly from server time
 * Returns the offset in milliseconds (positive = client ahead, negative = client behind)
 * @param forceRefresh - bypass cache and fetch fresh server time
 */
export const getClockDriftMs = async (forceRefresh = false): Promise<number> => {
  await getServerTime(forceRefresh); // Ensure offset is calculated
  return serverTimeOffset ?? 0;
};

/**
 * Check if clock drift is significant (> threshold minutes)
 */
export const hasSignificantClockDrift = async (thresholdMinutes: number = 5, forceRefresh = false): Promise<boolean> => {
  const drift = await getClockDriftMs(forceRefresh);
  return Math.abs(drift) > thresholdMinutes * 60 * 1000;
};

/**
 * Get clock drift in rounded minutes (absolute value)
 * @param forceRefresh - bypass cache and fetch fresh server time
 */
export const getClockDriftMinutes = async (forceRefresh = false): Promise<number> => {
  const driftMs = await getClockDriftMs(forceRefresh);
  return Math.round(Math.abs(driftMs) / 60000);
};

/**
 * Get local date string that represents "today" according to server
 */
export const getServerTodaySync = (): string => {
  // If we have a cached offset, use it; otherwise use local time
  if (serverTimeOffset !== null) {
    const serverNow = new Date(Date.now() + serverTimeOffset);
    return formatDateToYYYYMMDD(serverNow);
  }
  return formatDateToYYYYMMDD(new Date());
};
